import React from 'react'
import {PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend} from 'recharts'

// Define interfaces
export interface ChartDataItem {
  name: string
  value: number
  color: string
  actualValue?: number // Optional actual value for tooltip display
}

interface DonutChartProps {
  data: ChartDataItem[]
  height?: number
  innerRadius?: number
  outerRadius?: number
  itemLabel?: string
  calculatePercentage?: (value: number, total: number) => string
}

const DonutChart: React.FC<DonutChartProps> = ({
  data,
  height = 300,
  innerRadius = 60,
  outerRadius = 120,
  itemLabel = '',
  calculatePercentage,
}) => {
  // Calculate total value for percentage
  const total = data.reduce((sum, item) => sum + item.value, 0)

  // Default percentage calculation if not provided
  const getPercentage =
    calculatePercentage || ((value, total) => ((value / total) * 100).toFixed(1))

const renderCustomLabelLine = (props: any) => {
  const {
    cx,
    cy,
    midAngle,
    outerRadius,
    index,
  } = props

  const RADIAN = Math.PI / 180
  const startX = cx + outerRadius * Math.cos(-midAngle * RADIAN)
  const startY = cy + outerRadius * Math.sin(-midAngle * RADIAN)
  const endX = cx + (outerRadius + 15) * Math.cos(-midAngle * RADIAN)
  const endY = cy + (outerRadius + 15) * Math.sin(-midAngle * RADIAN)

  const color = data[index]?.color || '#000'

  return (
    <line
      x1={startX}
      y1={startY}
      x2={endX}
      y2={endY}
      stroke={color}
      strokeWidth={2}
    />
  )
}

  return (
    <ResponsiveContainer width='100%' height={height}>
      <PieChart>
        <Pie
          data={data}
          cx='50%'
          cy='50%'
          labelLine={renderCustomLabelLine}
          outerRadius={outerRadius}
          innerRadius={innerRadius}
          fill='#8884d8'
          dataKey='value'
          nameKey='name'
          label={({name, percent}) => `${(percent * 100).toFixed(0)}%`}
          paddingAngle={3}
          animationBegin={0}
          animationDuration={1000}
        >
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={entry.color} stroke='#ffffff' strokeWidth={2} />
          ))}
        </Pie>
        <Tooltip
          content={(props) => {
            const {active, payload} = props
            if (active && payload && payload.length) {
              // Type-safe access to custom properties
              const entry = payload[0]
              const color = entry.payload.color as string
              const name = entry.name
              // Use actualValue if available, otherwise use value
              const displayValue =
                entry.payload.actualValue !== undefined ? entry.payload.actualValue : entry.value
              const percentage = getPercentage(entry.payload.value, total)

              return (
                <div className='bg-white p-4 shadow-sm rounded custom-tooltip'>
                  <div className='fw-bold fs-6 mb-1'>
                    {name} {itemLabel}
                  </div>
                  <div className='d-flex align-items-center'>
                    <div
                      className='rounded me-2'
                      style={{
                        width: '10px',
                        height: '10px',
                        backgroundColor: color,
                      }}
                    ></div>
                    <div className='fw-bold fs-5'>
                      {displayValue} {itemLabel.toLowerCase()}
                    </div>
                  </div>
                  <div className='text-muted fs-7 mt-1'>{percentage}% of total</div>
                </div>
              )
            }
            return null
          }}
          wrapperStyle={{outline: 'none'}}
        />
        <Legend
          layout='horizontal'
          verticalAlign='bottom'
          align='center'
          iconSize={10}
          iconType='circle'
          formatter={(value, entry, index) => (
            <span className='fs-6 fw-semibold text-gray-700'>{value}</span>
          )}
        />
      </PieChart>
    </ResponsiveContainer>
  )
}

export default DonutChart
